import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Bot } from "lucide-react";
import Header from "@/components/Header";
import { getSessionToken } from "@/utils/auth";
import { parseUtc } from "@/components/messages/ReplyDraftCard";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${getSessionToken()}` } });
const when = (s) => (s ? parseUtc(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Los_Angeles" }) : "");
const STYLE = { pending: "bg-amber-100 text-amber-800", sent: "bg-green-100 text-green-800", declined: "bg-zinc-200 text-zinc-600",
  expired: "bg-zinc-100 text-zinc-500", superseded: "bg-zinc-100 text-zinc-500", office_handles: "bg-red-100 text-red-700", no_reply: "bg-sky-50 text-sky-700", failed: "bg-red-100 text-red-700" };

function Row({ d }) {
  return (
    <div className="p-3 space-y-1 text-sm" data-testid={`reply-draft-row-${d.draft_id}`}>
      <div className="flex items-center gap-2">
        <span className="font-semibold">{d.customer_name}</span>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${STYLE[d.status] || "bg-zinc-100"}`}>{d.status.replace("_", " ")}</span>
        {d.flag && <span className="text-[11px] text-amber-700">{d.flag}</span>}
        <span className="ml-auto text-xs text-zinc-400">#{d.code} · {when(d.created_at)}</span>
      </div>
      <div className="text-zinc-600">Customer: “{d.inbound_text}”</div>
      {d.draft && <div className="text-zinc-900">Draft: {d.draft}</div>}
      {d.ai_draft && <div className="text-[11px] italic text-zinc-500">Held-back AI text: {d.ai_draft}</div>}
      {d.sent_text && <div className="text-green-800">Sent{d.sent_text !== d.draft ? " (changed)" : ""}: {d.sent_text}</div>}
      <div className="text-[11px] text-zinc-500">
        Sources: {(d.sources || []).join(", ") || "none"}{d.reason ? ` · Why: ${d.reason}` : ""}
        {d.decision ? ` · Decision: ${d.decision}${d.decided_by ? ` by ${d.decided_by}` : ""}${d.decision_channel ? ` (${d.decision_channel})` : ""}` : ""}
      </div>
    </div>
  );
}

export default function ReplyDraftsLog() {
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    axios.get(`${API}/reply-drafts`, { ...auth(), params: { limit: 200, ...(filter ? { status: filter } : {}) } }).then((r) => setRows(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { axios.get(`${API}/reply-drafts/settings`, auth()).then((r) => setEnabled(r.data.enabled)).catch(() => {}); }, []);
  useEffect(load, [load]);
  const toggle = async () => {
    try { const { data } = await axios.put(`${API}/reply-drafts/settings`, { enabled: !enabled }, auth()); setEnabled(data.enabled); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };
  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        <button onClick={() => navigate("/settings")} data-testid="back-to-settings" className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900">
          <ArrowLeft className="w-4 h-4" /> Back to Settings
        </button>
        <h1 className="text-xl font-bold text-zinc-900 flex items-center gap-2"><Bot className="w-6 h-6 text-[#207024]" /> AI Reply Drafts</h1>
        {enabled !== null && (
          <div className="bg-white rounded-xl border p-4 flex items-center justify-between gap-3">
            <div className="text-sm text-zinc-600">Draft replies to customer texts that need one. Approve, edit or decline them in Messages. Nothing sends without approval; drafts expire after 2 hours.</div>
            <button onClick={toggle} data-testid="reply-drafts-toggle"
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${enabled ? "bg-[#207024] text-white" : "bg-zinc-200 text-zinc-700"}`}>
              {enabled ? "ON" : "OFF"}
            </button>
          </div>
        )}
        <div className="bg-white rounded-xl border overflow-hidden" data-testid="reply-drafts-log">
          <div className="p-3 border-b flex items-center justify-between text-sm">
            <span className="font-semibold">{rows.length} drafts</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 rounded border px-2 text-xs" data-testid="reply-drafts-filter">
              <option value="">All</option><option value="pending">Waiting</option><option value="sent">Sent</option>
              <option value="declined">Declined</option><option value="expired,superseded">Expired</option><option value="office_handles">Office handles</option><option value="no_reply">No reply needed</option>
            </select>
          </div>
          <div className="divide-y">
            {rows.length === 0 && <div className="p-6 text-center text-sm text-zinc-400">No drafts yet</div>}
            {rows.map((d) => <Row key={d.draft_id} d={d} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
